import type { VariantProps } from 'class-variance-authority'
import { cva } from 'class-variance-authority'

export { default as Button } from './Button.vue'

/*
 * `ZhButton` (BG §22.4) is the reference this recipe is held to, by Button.reference.spec.ts and the control-voice
 * block in controls.spec.ts. Colour and radius come from theme.css for free; type, case, height and state styling
 * are literal utilities, so they say the reference's words here.
 *
 * The control voice (BG §19.3): mono, uppercased in CSS (the text content keeps its own case, so screen readers
 * and specs still read words), system tracking, and the reference's weight. Every variant but `link` speaks it:
 * `link` is inline prose inside a sentence, where mono-uppercase would read as a defect.
 */
const VOICE = 'font-mono font-bold uppercase tracking-[var(--tracking-system)]'

/*
 * The reference's hover and press, for the two variants it offers (the command and its outlined secondary): hover
 * brightens, adds a dirty-white edge and the command's hover bloom on top of its glow, and shifts 1px as a glitch
 * rather than a lift. Press keeps that shift and compresses vertically (the base's `scale-y`). Like the reference's
 * `:hover:not(:disabled)`, every hover and press is `not-disabled:`: a disabled button takes the pointer (see the
 * base), so without the gate it would glow and shift under it.
 *
 * The hover shadow is written out per variant, not built from a template, because Tailwind only generates classes it
 * finds whole in the source. Both draw the same light: the command spells the edge as the reference does
 * (`--dirty-white`); the outlined secondary names it through the semantic layer (`--foreground`, which is dirty
 * white), as it already does for its type (`text-foreground`). The outline is the one that sits in dense rows next to
 * a status pill, where a screen's rendered classes name a state and never a raw brand hue (RunsView.theme.spec.ts).
 */
const REFERENCE_MOTION =
    'hover:not-disabled:translate-x-px hover:not-disabled:brightness-112 active:not-disabled:translate-x-px'
const REFERENCE_HOVER = `${REFERENCE_MOTION} hover:not-disabled:shadow-[var(--glow-cyan),-2px_0_0_var(--dirty-white),var(--glow-cyan-hover)]`
const REFERENCE_HOVER_OUTLINE = `${REFERENCE_MOTION} hover:not-disabled:shadow-[var(--glow-cyan),-2px_0_0_var(--foreground),var(--glow-cyan-hover)]`

/*
 * The reference's disabled state: desaturate and dim rather than a flat fade, and drop the glow. The base adds the
 * not-allowed cursor. The status variants keep their own rule (below), so it is not on the base.
 */
const REFERENCE_DISABLED = 'disabled:saturate-50 disabled:brightness-80 disabled:shadow-none'

/*
 * The base. Disabled shows the not-allowed cursor, so it carries no `pointer-events-none`: an element the pointer
 * passes through never shows its own cursor. The native `disabled` attribute is what keeps the button inert, as it
 * is for `ZhButton`; an `<a>` never matches `:disabled`, so anchors were never covered by that rule either.
 *
 * The reduced-motion cancels repeat the gates of the transforms they cancel (`not-disabled`, `not-aria-[haspopup]`)
 * so they are as specific as those, and win by coming later in the stylesheet.
 */
export const buttonVariants = cva(
    'focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 rounded-lg border border-transparent bg-clip-padding text-sm focus-visible:ring-3 aria-invalid:ring-3 active:not-disabled:not-aria-[haspopup]:scale-y-97 active:not-disabled:not-aria-[haspopup]:brightness-95 [&_svg:not([class*=size-])]:size-4 group/button inline-flex shrink-0 items-center justify-center whitespace-nowrap transition-all select-none disabled:cursor-not-allowed motion-reduce:transition-none motion-reduce:hover:not-disabled:translate-none motion-reduce:active:not-disabled:translate-none motion-reduce:active:not-disabled:not-aria-[haspopup]:scale-none [&_svg]:pointer-events-none [&_svg]:shrink-0',
    {
        variants: {
            variant: {
                /*
                 * BG §22.4 — the command: cyan ground edged in its own colour, black type, a slight cyan glow. The
                 * same command `ZhButton` draws on zerohuman.com/v2. Anchor or button, hover brightens; it never
                 * swaps the ground for a dimmer one.
                 */
                default: `${VOICE} bg-primary text-primary-foreground border-primary shadow-[var(--glow-cyan)] ${REFERENCE_HOVER} ${REFERENCE_DISABLED}`,
                /*
                 * The reference's outlined secondary (`ZhButton`'s `ghost`, matched by form rather than by name):
                 * transparent ground, dirty-white type, a visible input-coloured border and no glow at rest.
                 */
                outline: `${VOICE} border-input text-foreground bg-transparent ${REFERENCE_HOVER_OUTLINE} aria-expanded:bg-muted aria-expanded:text-foreground ${REFERENCE_DISABLED}`,
                secondary: `${VOICE} bg-secondary text-secondary-foreground hover:not-disabled:bg-secondary/80 aria-expanded:bg-secondary aria-expanded:text-secondary-foreground ${REFERENCE_DISABLED}`,
                ghost: `${VOICE} hover:not-disabled:bg-muted hover:not-disabled:text-foreground dark:hover:not-disabled:bg-muted/50 aria-expanded:bg-muted aria-expanded:text-foreground ${REFERENCE_DISABLED}`,
                /*
                 * The brand palette has no red, so danger cannot be a different hue from the primary
                 * (BG §18.2 — magenta is the only alert-weight colour there is). It is a different
                 * hue *and* form: the primary is filled cyan with black type, this is a magenta outline
                 * on the panel ground. Callers pair it with a warning glyph and a confirmation.
                 */
                destructive: `${VOICE} border-destructive text-destructive bg-transparent hover:not-disabled:bg-destructive/15 focus-visible:ring-destructive/40 focus-visible:border-destructive ${REFERENCE_DISABLED}`,
                link: 'text-primary font-medium underline-offset-4 hover:not-disabled:underline disabled:opacity-65',
                /*
                 * BG §18.4's status colours, for a button that reports where its own action stands: amber while
                 * it is in motion (or waiting to be brought up to date), green once it ended well, magenta once
                 * it ended badly. Status colours mean the same thing everywhere, so a screen asks for the
                 * state by name rather than restating a colour. A result is shown on a disabled button, so the
                 * two outcomes keep their full colour instead of the disabled fade. Pair each with a glyph and
                 * a word (BG §22.3): the colour is never the only signal.
                 */
                warning: `${VOICE} bg-warning-amber text-void-black hover:not-disabled:bg-warning-amber/85 disabled:opacity-65`,
                success: `${VOICE} bg-success-green text-void-black disabled:opacity-100`,
                failure: `${VOICE} bg-neon-magenta text-void-black shadow-[var(--glow-magenta)] disabled:opacity-100`,
            },
            /*
             * Corners come from the theme's `--radius-*` scale (all 0, BG §17.2) by name. Upstream capped the
             * small sizes with an arbitrary `min(…, 10px)` radius, a literal the theme could never reach.
             *
             * `default` is the reference's control: its height (the one `Input` and `SelectTrigger` already
             * share), inline padding and type size. `lg` stays a step above it. The compact sizes keep their
             * heights for dense surfaces (tables, copy buttons, graph overlays) and take only the voice; the
             * reference offers no compact size to match.
             */
            size: {
                default:
                    'h-13 gap-1.5 px-7 text-[0.8125rem] has-data-[icon=inline-end]:pr-6 has-data-[icon=inline-start]:pl-6',
                xs: 'h-6 gap-1 rounded-md px-2 text-xs in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*=size-])]:size-3',
                sm: 'h-7 gap-1 rounded-md px-2.5 text-[0.8rem] in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*=size-])]:size-3.5',
                lg: 'h-14 gap-1.5 px-8 has-data-[icon=inline-end]:pr-7 has-data-[icon=inline-start]:pl-7',
                icon: 'size-8',
                'icon-xs':
                    'size-6 rounded-md in-data-[slot=button-group]:rounded-lg [&_svg:not([class*=size-])]:size-3',
                'icon-sm': 'size-7 rounded-md in-data-[slot=button-group]:rounded-lg',
                'icon-lg': 'size-9',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    },
)
export type ButtonVariants = VariantProps<typeof buttonVariants>
